'use client'

import { type ReactNode, useMemo } from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useClickAction } from '@src/hooks/useClickAction/useClickAction'
import {
  type ActionName,
  type AnalyticsEvents,
} from '@src/hooks/useClickAction/useClickAction.builder'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { filterBuilderAttributes, getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

export type ActionLinkProps = UseClickActionArgs & {
  href?: string
  actionName?: ActionName
  target?: '_self' | '_blank'
  analyticsEvent?: (typeof AnalyticsEvents)[number]
  children?: ReactNode
  className?: string
}

/**
 * @todo remove this after old ActionLink is removed from all builder consumers
 */
function getClickActionProps(props: ActionLinkProps): UseClickActionArgs {
  const { href, target, analyticsEvent } = props
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  let actionName = props.actionName || 'none'
  if ((actionName as string) === 'link') actionName = 'navigate'
  return {
    ...props,
    actionName,
    navigate: {
      href: href ?? props?.navigate?.href,
      target: target ?? props?.navigate?.target,
      analyticsEvent: analyticsEvent ?? props?.navigate?.analyticsEvent,
    },
  }
}

export const ActionLink = (props: ActionLinkProps) => {
  const { children, attributes } = props
  const { edit } = getBuilderPreviewMode()
  const clickActionProps = useMemo(() => getClickActionProps(props), [props])
  const { renderVideo, handlers } = useClickAction(clickActionProps)

  const actionName = clickActionProps.actionName
  const isWrapping = actionName === 'navigate'

  const divButtonProps =
    !isWrapping && actionName && actionName !== 'none'
      ? {
          role: 'button',
          tabIndex: 0,
        }
      : {}

  return (
    <div
      className={cn(
        'cursor-pointer',
        { 'border-4 border-dotted border-sky-500': edit },
        props.className
      )}
    >
      {isWrapping ? (
        <LocalizedLink
          {...(filterBuilderAttributes(attributes) as any)}
          {...handlers}
          href={clickActionProps.navigate?.href ?? ''}
          target={clickActionProps.navigate?.target}
        >
          {children}
        </LocalizedLink>
      ) : (
        <div {...divButtonProps} {...handlers}>
          {children}
        </div>
      )}
      {renderVideo()}
    </div>
  )
}
